/**
 * Typed API hooks.
 *
 * Each hook wraps `apiFetch` + a zod schema + a query key. Components
 * call these hooks instead of `apiFetch` directly so caching, deduping,
 * background refetch, and dev-tools work for free.
 *
 * When the real backend ships, the implementations don't change — MSW
 * gets disabled and the same `fetch()` calls hit FastAPI.
 */

import { ApiError } from "@compliance/domain";
import {
  type UseQueryResult,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { z } from "zod";
import {
  type BuiltAllocation,
  allocationPeriodView,
  buildAllocation,
  dairyHistoryView,
  lotInventoryView,
  stationHistoryView,
} from "../lib/allocationView";
import { apiFetch, apiFetchBlob, apiFetchMultipart } from "../lib/api";
import { pickActiveBatch } from "../lib/batchState";
import { todayPacific } from "../lib/dateRange";
import { scfFigureAsMscf } from "../lib/displayUnits";
import { queryKeys } from "../lib/queryKeys";
import {
  allocationPreviewSchema,
  auditEntrySchema,
  backfillStatusSchema,
  batchDetailSchema,
  batchSummarySchema,
  calcResultEntrySchema,
  calibrationCertResponseSchema,
  calibrationCertSuggestionSchema,
  calibrationDueItemSchema,
  calibrationReadinessSchema,
  configDiffSchema,
  configVersionSchema,
  configVersionSummarySchema,
  confirmationPreviewSchema,
  correctionRecordSchema,
  dairyProductionRowSchema,
  dairyRecordSchema,
  equipmentRosterItemSchema,
  filedConfirmationSchema,
  filedUtilityStatementSchema,
  fseRecordSchema,
  issueSchema,
  lcfsPeriodValidationSchema,
  lcfsThroughputPreviewSchema,
  listResponseSchema,
  manualUploadRecordSchema,
  reportListItemSchema,
  reportMetadataSchema,
  reportPreviewSchema,
  seriesResponseSchema,
  sourcesCatalogResponseSchema,
  stationCapacityRowSchema,
  subscriptionStateSchema,
  substitutionMeterSchema,
  topicInfoSchema,
  transactionConfirmationRecordSchema,
  uploadAcceptResponseSchema,
  uploadIngestResponseSchema,
  uploadRowsResponseSchema,
  userAccessViewSchema,
  type userResponseSchema,
  utilityStatementRecordSchema,
  validationStartedSchema,
  validationStatusSchema,
  workbookVarianceSchema,
} from "../lib/schemas";
import { type SettledBaseline, observeSettled } from "../lib/settledRun";
import type {
  BatchConfigInput,
  BatchState,
  BatchSummary,
  Figure,
  NewStationInput,
  ProposeCorrectionRequest,
  SeriesResponse,
  UpdateConfigRequest,
  UploadStatus,
  UploadType,
  WorkbookComparisonRequest,
} from "./types";

// ── Health (public, no auth) ───────────────────────────────────────────────

// GET /health: the live BE returns status "ok" OR "degraded" on a 200 (e.g.
// s3_ok=false → "degraded"); hard failure is an HTTP 5xx caught by `isError`.
// The badge derives its visual from db_ok / s3_ok, not the status string, so we
// accept any status string here rather than a literal (a "degraded" 200 must
// not surface as a CONTRACT_VIOLATION that falsely reads "down").
const healthSchema = z.object({
  status: z.string(),
  software_version: z.string(),
  db_ok: z.boolean(),
  s3_ok: z.boolean(),
});

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: ({ signal }) => apiFetch("/health", healthSchema, { signal }),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

// ── Current user ───────────────────────────────────────────────────────────
//
// There is deliberately no hook here — read the current user with `useUser()`
// from `../auth`.
//
// `AuthProvider` owns the `queryKeys.currentUser` query, and a second fetcher on
// that key is not merely duplicated work but a hazard: both observers share one
// cache entry, so whichever URL is wrong poisons the other's result. This file
// kept calling `/users/me` after the identity endpoint moved to `/me`, which
// meant mounting the admin page 404'd the shared key and AuthProvider then read
// its own identity as a deterministic failure and rendered AccountInactive.

// ── Admin: change a user's role (PATCH /users/{user_id}/role) ──────────────
//
// Admin-only mutation. X-Reason is required (handled by apiFetch). The BE
// returns the updated product-access row (userAccessViewSchema, keyed by
// `user_id`), NOT the /users/me envelope. On success, callers should
// invalidate `queryKeys.currentUser` in case the admin changed their own role —
// RequireRole will re-evaluate and may redirect them.

export type UserRoleValue = z.infer<typeof userResponseSchema.shape.role>;

export function usePatchUserRole() {
  return useMutation({
    mutationFn: ({
      userId,
      role,
      reason,
    }: {
      userId: string;
      role: UserRoleValue;
      reason: string;
    }) =>
      apiFetch(`/users/${userId}/role`, userAccessViewSchema, {
        method: "PATCH",
        body: { role },
        reason,
      }),
  });
}

// ── Batches ────────────────────────────────────────────────────────────────

const batchListSchema = listResponseSchema(batchSummarySchema);

export function useBatches() {
  return useQuery({
    queryKey: queryKeys.batches.list(),
    queryFn: ({ signal }) => apiFetch("/batches", batchListSchema, { signal }),
  });
}

export function useBatch(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.batches.detail(batchId),
    queryFn: ({ signal }) => apiFetch(`/batches/${batchId}`, batchDetailSchema, { signal }),
    enabled: !!batchId,
  });
}

/**
 * Create a new RFS batch for a month (POST /batches). The backend opens the
 * batch for the chosen `period` (YYYY-MM), pins the config snapshot, and
 * autopopulates the month's figures from the DUT feed. No X-Reason (opening a
 * batch is a routine step); Idempotency-Key auto. Operations/reviewer/admin only
 * (auditor is read-only, enforced server-side). Returns the created BatchMetadata
 * (→ batchDetailSchema).
 *
 * A 400 VALIDATION_ERROR carrying `details.existing_batch_id` means a batch
 * already exists for that month — callers surface the conflict and link to the
 * existing batch rather than retrying.
 */
export function useCreateBatch() {
  return useMutation({
    mutationFn: ({
      period,
      batch_config,
    }: {
      period: string;
      /** Optional per-batch overrides; any field omitted defaults from config. */
      batch_config?: BatchConfigInput;
    }) =>
      apiFetch("/batches", batchDetailSchema, {
        method: "POST",
        body: { program: "rfs", period, ...(batch_config ? { batch_config } : {}) },
      }),
  });
}

/**
 * Resolve "the active batch" from the live batch list, with no hardcoded
 * fixture ID.
 *
 * The active batch is the one a global view (audit, issues, reports
 * library) defaults to before cross-batch UI lands. Preference order:
 *   1. The most-recently-created Open batch (the one ops is currently
 *      filling).
 *   2. Otherwise the most-recently-created batch overall (so demos with
 *      only closed fixtures still have something to point at).
 *
 * Returns null `batchId` until the batches list resolves; consumers must
 * gate their detail fetches accordingly.
 */
export function useActiveBatchId(): {
  batchId: string | null;
  isPending: boolean;
  isError: boolean;
} {
  const list = useBatches();
  if (list.isPending) return { batchId: null, isPending: true, isError: false };
  if (list.isError || !list.data) return { batchId: null, isPending: false, isError: true };
  const active = pickActiveBatch(list.data.items);
  return { batchId: active?.batch_id ?? null, isPending: false, isError: false };
}

/**
 * Per-batch calc results. The BE returns one row per calc kind (e.g.
 * `rfs_token_mmbtu_hhv`, `total_hub_scf`). The workpaper + the Batches
 * page KPIs select figures by kind.
 *
 * The `BatchMetadata` envelope does NOT carry these — the FE composes
 * by fetching both. Empty list while the calc engine hasn't run for a
 * batch (e.g. open period with no closing run yet).
 */
const calcResultsResponseSchema = z.array(calcResultEntrySchema);

export function useBatchCalcResults(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.batches.calcResults(batchId),
    queryFn: ({ signal }) =>
      apiFetch(`/batches/${batchId}/calc-results`, calcResultsResponseSchema, { signal }),
    enabled: !!batchId,
  });
}

/**
 * Index calc-result rows by `kind` so callers can read the headline
 * figures by name (`byKind.get("rfs_token_mmbtu_hhv")?.figure.value`).
 * Returns a Map keyed on the kind string.
 */
export function indexCalcResultsByKind(
  rows: ReadonlyArray<{ kind: string; figure: { value: string; basis: string } }>,
): Map<string, { value: string; basis: string }> {
  const out = new Map<string, { value: string; basis: string }>();
  for (const r of rows) out.set(r.kind, r.figure);
  return out;
}

/**
 * Resolve a batch's headline RFS figures: prefer the inline `totals` envelope
 * (mock fixtures carry it; the real BatchMetadata doesn't), else fall back to
 * the per-kind calc-results. The calc-results fetch is skipped when `totals`
 * are present, and TanStack Query caches across callers so concurrent callers
 * share one request. The single home for the calc-kind → figure mapping —
 * `BatchDetail` extends `BatchSummary`, so detail callers can pass through too.
 */
export function useBatchFigures(batch: BatchSummary) {
  const calcQ = useBatchCalcResults(batch.totals ? undefined : batch.batch_id);
  const byKind = indexCalcResultsByKind(calcQ.data ?? []);
  const get = (kind: string, fallback?: Figure) =>
    (byKind.get(kind) as Figure | undefined) ?? fallback;
  return {
    // With `totals` present we serve them synchronously and never fetch, so
    // the disabled query's `isPending` (always true) must not leak out.
    isPending: batch.totals ? false : calcQ.isPending,
    // SCF on the wire (the filed unit), MSCF on screen — see lib/displayUnits.
    totalHubMscf: scfFigureAsMscf(batch.totals?.total_hub_scf ?? get("total_hub_scf")),
    tokenMmbtu: batch.totals?.token_mmbtu ?? get("rfs_token_mmbtu_hhv"),
    utilityMmbtu: batch.totals?.utility_mmbtu ?? get("rfs_utility_mmbtu_hhv"),
    eligibleMmbtu: batch.totals?.eligible_mmbtu ?? get("rfs_eligible_mmbtu_hhv"),
    rfGallons: batch.totals?.rf_gallons ?? get("rfs_rf_gallons"),
    d3Rins: batch.totals?.d3_rins ?? get("rfs_assigned_d3_rins"),
  };
}

const issueListSchema = listResponseSchema(issueSchema);

// API_CONTRACT §15: issues live at GET /issues (filtered by batch_id),
// NOT under /batches/{id}/issues — issues are a top-level resource.
export function useBatchIssues(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.batches.issues(batchId),
    queryFn: ({ signal }) =>
      apiFetch("/issues", issueListSchema, {
        signal,
        query: { batch_id: batchId },
      }),
    enabled: !!batchId,
  });
}

/**
 * Whether the batch's findings still describe its current data.
 *
 * Polls while a run is in flight: `POST /validate` returns 202 with no
 * completion signal, so this is how the UI learns a background run finished
 * rather than telling the user to press the button again.
 */
export function useValidationStatus(batchId: string | undefined) {
  const queryClient = useQueryClient();
  // What was already terminal when this hook first saw the batch, so the
  // invalidation below fires once per NEW run rather than on every poll after
  // one finishes. Holds the batch too, so switching batches re-baselines
  // instead of comparing run ids across two different batches.
  const seen = useRef<SettledBaseline | null>(null);

  const query = useQuery({
    queryKey: queryKeys.batches.validationStatus(batchId),
    queryFn: ({ signal }) =>
      apiFetch(`/batches/${batchId}/validation-status`, validationStatusSchema, {
        signal,
      }),
    enabled: !!batchId,
    refetchInterval: (q) => (q.state.data?.running ? 5000 : false),
  });

  // When polling observes a run reach a terminal state, refresh the worklist it
  // just rewrote. IssuesTab's fixed 5s/15s invalidations are a guess at how long a
  // background run takes; a slower one lands after the last of them, and then this
  // query alone refetches — so the page would say validation is current while still
  // listing the pre-run findings until a focus event or reload.
  // `undefined` while the status query has no answer yet — see `observeSettled`.
  const run = query.data?.last_run;
  const terminal =
    query.data === undefined ? undefined : run && run.status !== "running" ? run.id : null;
  useEffect(() => {
    if (!batchId) return;
    const { baseline, isNew } = observeSettled(seen.current, batchId, terminal);
    seen.current = baseline;
    if (!isNew) return;
    void queryClient.invalidateQueries({
      queryKey: queryKeys.batches.issues(batchId),
    });
  }, [batchId, terminal, queryClient]);

  return query;
}

const auditListSchema = listResponseSchema(auditEntrySchema);

// API_CONTRACT §15: audit is a top-level resource queried by batch_id.
export function useBatchAudit(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.batches.audit(batchId),
    queryFn: ({ signal }) =>
      apiFetch("/audit", auditListSchema, {
        signal,
        query: { batch_id: batchId },
      }),
    enabled: !!batchId,
  });
}

// ── Audit export (real BE: GET /audit/export, CSV stream) ──────────────────
//
// Unlike /issues/export, /audit/export returns the CSV bytes directly
// (no signed-URL envelope). The hook returns a Blob; the caller wires
// it to an anchor click.

export function useExportAudit() {
  return useMutation({
    mutationFn: async ({ batchId }: { batchId?: string }) => {
      const query = batchId ? { batch_id: batchId } : undefined;
      return apiFetchBlob("/audit/export", { query });
    },
  });
}

const reportListSchema = listResponseSchema(reportListItemSchema);

export function useBatchReports(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.batches.reports(batchId),
    queryFn: ({ signal }) => apiFetch(`/batches/${batchId}/reports`, reportListSchema, { signal }),
    enabled: !!batchId,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────

/**
 * Sign-off a batch. Sends X-Reason + Idempotency-Key (auto). Requires the
 * caller to be reviewer or admin (enforced server-side).
 *
 * Backend rejects with 422 VALIDATION_BLOCKERS if any must-resolve issues
 * (open critical/high) remain on the batch — see API_CONTRACT §11.
 */
// ── Batch lifecycle (Layer 3) ───────────────────────────────────────────────
//
// The state-machine actions beyond sign-off. All require X-Reason (handled by
// apiFetch) and are role-gated server-side (auditor is read-only):
//   - transition: the generic editing lane (Open ↔ PendingApproval)
//   - submit: Approved → Submitted (auto-frozen); records the EMTS Submit ID
//   - restate: Submitted/Frozen → a new `-R{n}` batch at Open (parent preserved)
//
// transition + restate return BatchMetadata (→ batchDetailSchema). submit
// returns the persisted SubmitRecord, NOT the batch — callers invalidate the
// batch detail/list afterward to pick up the server-side state flip.

export function useTransitionBatch() {
  return useMutation({
    mutationFn: ({
      batchId,
      toState,
      reason,
    }: {
      batchId: string;
      toState: BatchState;
      reason: string;
    }) =>
      apiFetch(`/batches/${batchId}/transition`, batchDetailSchema, {
        method: "POST",
        reason,
        body: { to_state: toState },
      }),
  });
}
// ── Corrections (Layer 2) ───────────────────────────────────────────────────
//
// Propose → approve/reject workflow over a batch's input data. GET returns a
// bare array (no list envelope). Propose is allowed for any non-auditor role;
// approve/reject are reviewer/admin (enforced server-side). All mutations
// require X-Reason; approve/reject also accept an optional decision note.

const correctionListSchema = z.array(correctionRecordSchema);

export function useBatchCorrections(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.batches.corrections(batchId),
    queryFn: ({ signal }) =>
      apiFetch(`/batches/${batchId}/corrections`, correctionListSchema, { signal }),
    enabled: !!batchId,
  });
}

export function useProposeCorrection() {
  return useMutation({
    mutationFn: ({
      batchId,
      input,
      reason,
    }: {
      batchId: string;
      input: ProposeCorrectionRequest;
      reason: string;
    }) =>
      apiFetch(`/batches/${batchId}/corrections`, correctionRecordSchema, {
        method: "POST",
        reason,
        body: input,
      }),
  });
}

export function useApproveCorrection() {
  return useMutation({
    mutationFn: ({
      correctionId,
      note,
      reason,
    }: {
      correctionId: string;
      note?: string;
      reason: string;
    }) =>
      apiFetch(`/corrections/${correctionId}/approve`, correctionRecordSchema, {
        method: "POST",
        reason,
        body: note ? { note } : {},
      }),
  });
}

export function useRejectCorrection() {
  return useMutation({
    mutationFn: ({
      correctionId,
      note,
      reason,
    }: {
      correctionId: string;
      note?: string;
      reason: string;
    }) =>
      apiFetch(`/corrections/${correctionId}/reject`, correctionRecordSchema, {
        method: "POST",
        reason,
        body: note ? { note } : {},
      }),
  });
}

// ── Substitution meter (Layer 2 — F6.6) ─────────────────────────────────────

export function useSubstitutionMeter(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.batches.substitutionMeter(batchId),
    queryFn: ({ signal }) =>
      apiFetch(`/batches/${batchId}/substitution-meter`, substitutionMeterSchema, { signal }),
    enabled: !!batchId,
  });
}

// ── Restatement chain (Layer 3) — list[BatchMetadata], oldest → newest ───────

// ── Config (customer profile versioning) ─────────────────────────────────────
//
// Reads are open to any compliance role. Creating a version is admin-only and
// requires X-Reason. `GET /config/active` resolves the single live config from
// the authenticated session's organization, so callers need no profile key up
// front (each deployment is single-tenant but customer-agnostic). `profileKey`
// stays available for the version-list/diff queries that still scope by it.

const configVersionListSchema = z.array(configVersionSummarySchema);

export function useActiveConfig(profileKey?: string, at?: string) {
  return useQuery({
    queryKey: queryKeys.config.active(profileKey, at),
    queryFn: ({ signal }) =>
      apiFetch("/config/active", configVersionSchema, {
        query: { profile_key: profileKey, at },
        signal,
      }),
  });
}

export function useConfigVersions(profileKey: string | undefined) {
  return useQuery({
    queryKey: queryKeys.config.versions(profileKey),
    queryFn: ({ signal }) =>
      apiFetch("/config/versions", configVersionListSchema, {
        query: { profile_key: profileKey },
        signal,
      }),
    enabled: !!profileKey,
  });
}

export function useConfigVersion(versionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.config.version(versionId),
    queryFn: ({ signal }) =>
      apiFetch(`/config/versions/${versionId}`, configVersionSchema, { signal }),
    enabled: !!versionId,
  });
}

export function useConfigDiff(fromId: string | undefined, toId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.config.diff(fromId, toId),
    queryFn: ({ signal }) =>
      apiFetch("/config/versions/diff", configDiffSchema, {
        query: { from_id: fromId, to_id: toId },
        signal,
      }),
    enabled: !!fromId && !!toId,
  });
}

/**
 * Edit the single live configuration in place (PATCH /config). Admin-only
 * (BE-enforced), X-Reason required. Replaces the live config's content; editing a
 * constant recomputes all batches, while the per-batch defaults only change the
 * pre-fill for new batches. Returns the updated config (→ configVersionSchema).
 */
export function useUpdateConfig() {
  return useMutation({
    mutationFn: ({ input, reason }: { input: UpdateConfigRequest; reason: string }) =>
      apiFetch("/config", configVersionSchema, {
        method: "PATCH",
        reason,
        body: input,
      }),
  });
}

// ── Issues export (POST /issues/export) — per API_CONTRACT §12.1 ────────────
//
// Returns a signed-URL response so the caller can download the CSV from S3.
// X-Reason + Idempotency-Key required (handled by apiFetch).
//
// Response fields per the contract: export_id, format, url, expires_at,
// sha256, content_length, row_count, generated_at, filter_applied. We
// schema-validate the ones we use and leave the rest as `unknown` so the
// BE can evolve without breaking us.

const issuesExportResponseSchema = z.object({
  export_id: z.string(),
  format: z.string().optional(),
  url: z.string(),
  expires_at: z.string(),
  sha256: z.string(),
  content_length: z.number().optional(),
  row_count: z.number(),
  generated_at: z.string().optional(),
  filter_applied: z.unknown().optional(),
});

/**
 * Filter shape mirrors API_CONTRACT §12.1 (arrays where appropriate).
 * Empty / missing fields are omitted from the wire payload — `undefined`
 * values stay out of the JSON.
 */
export type IssuesExportFilter = {
  batch_id?: string;
  severity?: ReadonlyArray<"critical" | "high" | "medium" | "informational">;
  family?: ReadonlyArray<string>;
  status?: ReadonlyArray<"open" | "acknowledged" | "resolved">;
  source?: ReadonlyArray<string>;
  assignee?: string;
  period?: { from?: string; to?: string };
};

export function useExportIssues() {
  return useMutation({
    mutationFn: ({ filter, reason }: { filter: IssuesExportFilter; reason: string }) =>
      apiFetch("/issues/export", issuesExportResponseSchema, {
        method: "POST",
        reason,
        // Contract requires `format: "csv"` (XLSX is deferred).
        body: { filter, format: "csv" },
      }),
  });
}

// ── Issue mutations (BE: POST /issues/{id}/{acknowledge|assign|resolve}) ────
//
// Reviewer/admin only on the BE; FE mirrors via the IssueDrawer's
// button visibility. All three return the updated IssueRecord, which
// callers should push into the cache via setQueryData or invalidate.

export function useAcknowledgeIssue() {
  return useMutation({
    mutationFn: ({ issueId, reason }: { issueId: string; reason: string }) =>
      apiFetch(`/issues/${issueId}/acknowledge`, issueSchema, {
        method: "POST",
        reason,
        body: {},
      }),
  });
}

export function useAssignIssue() {
  return useMutation({
    mutationFn: ({
      issueId,
      assigneeUserId,
      reason,
    }: {
      issueId: string;
      assigneeUserId: string;
      reason: string;
    }) =>
      apiFetch(`/issues/${issueId}/assign`, issueSchema, {
        method: "POST",
        reason,
        // BE AssignRequest is `{ assignee: UUID }` with extra="forbid" — the
        // field MUST be named `assignee` or the request 422s.
        body: { assignee: assigneeUserId },
      }),
  });
}

/**
 * Evidence kinds the BE accepts when resolving a must-resolve finding
 * (BIO-308 #2). Which ones apply depends on the finding's family — a
 * calibration finding is not answered by an upload — and the server is
 * authoritative; `evidenceKindsFor` below mirrors its rules so the UI can offer
 * the right choices rather than letting the reviewer fill in a doomed form.
 */
export type EvidenceKind = "calibration_record" | "upload" | "correction" | "authorized_override";

export function useResolveIssue() {
  return useMutation({
    mutationFn: ({
      issueId,
      resolutionNote,
      reason,
      evidenceKind,
      evidenceRef,
    }: {
      issueId: string;
      resolutionNote?: string;
      reason: string;
      evidenceKind?: EvidenceKind;
      evidenceRef?: string;
    }) =>
      apiFetch(`/issues/${issueId}/resolve`, issueSchema, {
        method: "POST",
        reason,
        // BE ResolveRequest field is `note` (and extra=forbid, so the old
        // `resolution_note` would 422). The response still carries
        // `resolution_note` — only the request body differs.
        body: {
          ...(resolutionNote ? { note: resolutionNote } : {}),
          ...(evidenceKind ? { evidence_kind: evidenceKind } : {}),
          ...(evidenceRef ? { evidence_ref: evidenceRef } : {}),
        },
      }),
  });
}

// ── Artifact signed URL (POST /artifacts/{kind}/{id}/signed-url) ────────────
// Per API_CONTRACT.md §12 line 218: `kind` is one of the artifact kinds the
// BE knows how to sign. `issues_export` is NOT one of them — that flow goes
// through POST /issues/export which returns its own signed-URL envelope.
export type ArtifactKind = "report" | "calibration_cert" | "source_upload";

const signedUrlResponseSchema = z.object({
  url: z.string(),
  expires_at: z.string(),
  sha256: z.string(),
  content_length: z.number(),
});

export function useArtifactSignedUrl() {
  return useMutation({
    mutationFn: ({
      kind,
      id,
      reason,
    }: {
      kind: ArtifactKind;
      id: string;
      reason: string;
    }) =>
      apiFetch(`/artifacts/${kind}/${id}/signed-url`, signedUrlResponseSchema, {
        method: "POST",
        reason,
        body: {},
      }),
  });
}

// ── Report generate ─────────────────────────────────────────────────────────

const emtsGenerateResponseSchema = z.array(reportMetadataSchema);

// Report LIST endpoints (GET) return the deployed BE's ReportGenerationRecord
// shape (`id`/`config_version_id`, nullable `generated_at`/`sha256`), which the
// strict generate schema rejects → CONTRACT_VIOLATION → the download button
// never renders. Use the lenient list-item schema (the fix from commit 60ff0a0,
// originally only applied to the per-batch list) for quarterly + annual too.
// Bare array (no cursor), so NOT wrapped in listResponseSchema (BIO-144 #2).
// Cast away the transform's input≠output mismatch the same way listResponseSchema
// does, so apiFetch's z.ZodType<TResponse> infers the array OUTPUT type.
const reportListArraySchema = z.array(reportListItemSchema) as unknown as z.ZodType<
  z.infer<typeof reportListItemSchema>[]
>;

/**
 * Generate the EMTS Report-6 batch record (token generation + RIN entry
 * + monthly summary). Per the live BE, this is now a dedicated path
 * `/reports/emts/generate` rather than the previous generic
 * `/reports/{short_name}/generate`. The response is an array of three
 * ReportMetadata records (one per artifact).
 *
 * The MSW handler at the old `{short_name}` path still answers the
 * generic shape — UI like ReportsTab.tsx (which calls per-row generate
 * with a `short_name`) keeps working until that view is migrated to the
 * dedicated emts call.
 */
export function useGenerateEmtsReport() {
  return useMutation({
    mutationFn: ({ batchId }: { batchId: string }) =>
      apiFetch(`/batches/${batchId}/reports/emts/generate`, emtsGenerateResponseSchema, {
        method: "POST",
        body: {},
      }),
  });
}

// There is deliberately no per-short-name generate hook. One existed, posting to
// /batches/{id}/reports/{short_name}/generate for the per-row buttons in
// ReportsTab, and that route was never built: only an MSW handler answered it,
// so every click against a real API 404'd while the whole test suite stayed
// green. Generation is per SET here — /reports/emts/generate,
// /reports/rfs/generate-async, /reports/lcfs/generate-async, and the
// quarter/year form endpoints below.

// ── Uploads (Layer 1, MSW-first; BE delivery in flight) ────────────────────
//
// Per API_CONTRACT §15: POST /uploads/{type} multipart/form-data; GET
// /uploads list; GET /uploads/templates/{type}/blank direct CSV. Per
// TICKETS_LAYER_0_1 L1-FE-01: signed URLs are NOT used on the upload
// path — only on download paths (templates here use a direct GET).

const uploadListSchema = listResponseSchema(manualUploadRecordSchema);

/**
 * List uploads for a reporting period. The BE filters by `period` (YYYY-MM),
 * `upload_type`, and `status` — NOT by batch_id (uploads aren't batch-scoped
 * until accepted). The portal passes the selected batch's period; the
 * "latest per type" selection then filters this period's list client-side.
 * Disabled until a period is supplied.
 */
export function useUploads(period?: string) {
  return useQuery({
    queryKey: queryKeys.uploads.list(period),
    queryFn: ({ signal }) =>
      apiFetch("/uploads", uploadListSchema, {
        signal,
        query: { period, limit: 500 },
      }),
    enabled: !!period,
  });
}

/**
 * Cross-batch upload history. The BE's GET /uploads has no cursor — it returns
 * the newest `limit` rows (max 500) filtered by period / upload_type / status.
 * So this is a single bounded query, not an infinite one; the page renders the
 * list directly. If volume ever outgrows 500, the BE adds offset paging and
 * this hook grows a page param then.
 */
export type UploadHistoryFilter = {
  type?: UploadType;
  /** yyyy-mm reporting period. */
  period?: string;
  status?: UploadStatus;
};

export function useUploadHistory(filter: UploadHistoryFilter) {
  return useQuery({
    queryKey: [...queryKeys.uploads.all, "history", filter] as const,
    queryFn: ({ signal }) =>
      apiFetch("/uploads", uploadListSchema, {
        signal,
        query: {
          // BE param is `upload_type`, not `type`.
          upload_type: filter.type,
          period: filter.period,
          status: filter.status,
          limit: 500,
        },
      }),
  });
}

/**
 * Fetch a single upload by id. Powers the detail deep-link and the
 * DuplicateUploadPrompt's "existing upload" card.
 *
 * Maps to GET /uploads/{upload_id}. An unknown id is `400 VALIDATION_ERROR`
 * on the BE ("No upload with that id." — §11 has no NOT_FOUND), so we treat
 * that (and a legacy 404) as `null` for the not-found UX.
 */
export function useUpload(uploadId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.uploads.detail(uploadId),
    queryFn: async ({ signal }) => {
      try {
        return await apiFetch(`/uploads/${uploadId}`, manualUploadRecordSchema, { signal });
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.httpStatus === 404) return null;
          if (err.httpStatus === 400 && /no upload with that id/i.test(err.message)) return null;
        }
        throw err;
      }
    },
    enabled: !!uploadId,
    // No polling: the BE parses inline on POST, so an upload's status is
    // terminal once persisted — a `received` row is a parse failure, not an
    // in-flight scan, and nothing advances it without a new user action
    // (re-upload/replace/accept), each of which invalidates this query.
  });
}

/**
 * Upload a file as multipart/form-data to POST /uploads/{type}.
 *
 * The BE form fields are `period` (YYYY-MM) + `file` — the upload is NOT
 * batch-scoped at this point; linking to a batch happens later via
 * POST /uploads/{id}/accept. X-Reason + Idempotency-Key required.
 *
 * The BE responds with:
 *   - 200/201 + UploadIngestResult. Use `parse_ok` / `status` to branch:
 *       - parse_ok (status "parsed") → parse clean; offer Accept
 *       - !parse_ok (status "rejected") → `row_errors` block accept; fix + re-upload
 *   - 422 UPLOAD_MALWARE_DETECTED (ClamAV rejected the file; no parse ran)
 *   - 409 DUPLICATE_UPLOAD_CONFLICT (same (period, type) holds different bytes;
 *     details carries existing_upload_id/existing_sha256/incoming_sha256 — the
 *     UI consumes these in the merge/replace/cancel prompt)
 *   - 400 VALIDATION_ERROR (malformed body; missing file/period)
 */
export function useUploadFile() {
  return useMutation({
    mutationFn: ({
      type,
      period,
      file,
      reason,
    }: {
      type: UploadType;
      period: string;
      file: File;
      reason: string;
    }) => {
      const form = new FormData();
      form.append("file", file);
      form.append("period", period);
      return apiFetchMultipart(`/uploads/${type}`, form, uploadIngestResponseSchema, {
        reason,
      });
    },
  });
}

/**
 * File a calibration certificate: POST /uploads/calibration-cert, multipart,
 * the PDF plus the five fields a PDF cannot carry.
 *
 * One call does three things — archives the certificate, writes the
 * `calibration_records` row pointing at it, and resolves every open calibration
 * finding that record now covers. The response names both the record and the
 * findings it closed.
 *
 * Errors:
 *   - 400 VALIDATION_ERROR — not a PDF, or an unknown/retired equipment tag
 *     (`details.retired` tells the two apart). Nothing was archived.
 *   - 422 VALIDATION_ERROR — next_due_on on or before calibrated_on, or a
 *     calibration dated in the future.
 *   - 409 CONCURRENT_MODIFICATION — this instrument already has a record for
 *     that date; `details.existing_calibration_id` names it.
 *   - 409 DUPLICATE_UPLOAD_CONFLICT — this exact file is already filed.
 *   - 503 UNAVAILABLE — the archive failed, so nothing was recorded. Retry.
 */
export function useFileCalibrationCert() {
  return useMutation({
    mutationFn: ({
      file,
      reason,
      ...fields
    }: {
      file: File;
      reason: string;
      equipment_tag: string;
      calibrated_on: string;
      next_due_on: string;
      certificate_number?: string;
      certificate_issuer?: string;
      notes?: string;
    }) => {
      const form = new FormData();
      form.append("file", file);
      for (const [key, value] of Object.entries(fields)) {
        if (value) form.append(key, value);
      }
      return apiFetchMultipart("/uploads/calibration-cert", form, calibrationCertResponseSchema, {
        reason,
      });
    },
  });
}

/**
 * Read a dropped certificate's fields to pre-fill the form
 * (POST /uploads/calibration-cert/extract).
 *
 * Writes nothing — no archive, no record, no resolution. The operator confirms
 * what comes back and files through `useFileCalibrationCert`, which is where the
 * validation and the audit trail live. A failure here is not fatal: the form
 * stays usable and the fields are typed by hand.
 */
export function useExtractCalibrationCert() {
  return useMutation({
    mutationFn: ({ file }: { file: File }) => {
      const form = new FormData();
      form.append("file", file);
      return apiFetchMultipart(
        "/uploads/calibration-cert/extract",
        form,
        calibrationCertSuggestionSchema,
        { reason: "Read certificate fields" },
      );
    },
  });
}

/**
 * The active instrument roster (GET /equipment) for the certificate form's
 * picker. Deliberately not `useCalibrationDue`: that one answers "what needs
 * attention?", so an instrument in good standing — the ordinary case for a
 * routine certificate — is absent from it.
 */
export function useEquipmentRoster() {
  return useQuery({
    queryKey: ["equipment", "roster"],
    queryFn: ({ signal }) => apiFetch("/equipment", z.array(equipmentRosterItemSchema), { signal }),
    staleTime: 5 * 60_000,
  });
}

/**
 * Upload a file to POST /uploads with NO declared type or period — the BE
 * matches the template off the file's header row and reads the reporting
 * month from its period column, before anything is archived. Same response
 * shape as the typed route, with `detected: true`.
 *
 * Errors beyond the typed route's:
 *   - 400 VALIDATION_ERROR with `details.detection` — unrecognized header,
 *     missing/invalid/ambiguous period, unreadable file. `details` carries the
 *     structured diff (observed_header / closest_template / expected_header /
 *     missing_columns / unexpected_columns) the portal renders. Nothing was
 *     stored — a refused file leaves no record.
 *   - 409 DUPLICATE_UPLOAD_CONFLICT — `details` also carries the incumbent's
 *     `upload_type` + `period` now, since this client never declared either.
 *   - 413 — over the server's 10 MB ceiling.
 */
export function useUploadAutoFile() {
  return useMutation({
    mutationFn: ({ file, reason }: { file: File; reason: string }) => {
      const form = new FormData();
      form.append("file", file);
      return apiFetchMultipart("/uploads", form, uploadIngestResponseSchema, {
        reason,
      });
    },
  });
}

/**
 * Replace an existing upload (Replace path). POST /uploads/{id}/replace as
 * multipart with the new `file` plus `upload_type` + `period` (which must match
 * the displaced upload). The BE archives the new file, sets `replaces_upload_id`
 * on it, and marks the old record `superseded`. `reason` → X-Reason (required).
 *
 * Targets POST /uploads/{id}/replace as multipart/form-data with the new
 * file. `reason` becomes the X-Reason header — required by the BE for
 * audit-log entries on this audit-critical mutation.
 *
 * Response: 200 + UploadIngestResult describing the replacement. The old
 * record's status becomes `superseded` (the BE has only a backward
 * `replaces_upload_id` on the new record — no forward pointer), so the
 * active-upload picker skips records with status "superseded".
 *
 * Errors:
 *   - 400 REASON_REQUIRED — caller omitted the reason text
 *   - 400 VALIDATION_ERROR — unknown id, or mismatched upload_type/period
 *   - 404 NOT_FOUND — the existing upload_id was unknown
 *   - 415 UNSUPPORTED_MEDIA_TYPE — file MIME outside the type's whitelist
 *   - 422 UPLOAD_MALWARE_DETECTED — ClamAV flagged the bytes
 */
export function useReplaceUpload() {
  return useMutation({
    mutationFn: ({
      existingUploadId,
      uploadType,
      period,
      file,
      reason,
    }: {
      existingUploadId: string;
      uploadType: UploadType;
      period: string;
      file: File;
      reason: string;
    }) => {
      const form = new FormData();
      form.append("file", file);
      form.append("upload_type", uploadType);
      form.append("period", period);
      return apiFetchMultipart(
        `/uploads/${existingUploadId}/replace`,
        form,
        uploadIngestResponseSchema,
        { reason },
      );
    },
  });
}

/**
 * Download a pre-populated template CSV for the given upload type +
 * period. Per L1-BE-05 the BE serves these as a direct GET with
 * `Content-Disposition: attachment` — no signed-URL preflight. We use
 * apiFetchBlob so the bearer is attached and the response streams
 * straight to a Blob; the caller wires it to an anchor click.
 */
export function useDownloadTemplate() {
  return useMutation({
    mutationFn: ({ type, period }: { type: UploadType; period: string }) =>
      apiFetchBlob(`/uploads/templates/${type}/blank`, { query: { period } }),
  });
}

// ── Batch calibration readiness (BE: GET /batches/{id}/calibration-readiness)
//
// Whether every instrument the batch files on was in calibration for the whole
// period. `blocking_count > 0` is exactly what the BE's transition gate refuses
// on, so the approval action reads its disabled state from this one number
// rather than re-deriving a rule the server owns (BIO-309).

export function useCalibrationReadiness(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.batches.calibrationReadiness(batchId),
    queryFn: ({ signal }) =>
      apiFetch(`/batches/${batchId}/calibration-readiness`, calibrationReadinessSchema, {
        signal,
      }),
    enabled: !!batchId,
  });
}

// ── Report preview (BE: GET /reports/{id}/preview) ─────────────────────────
//
// The artifact's own cells, rendered server-side from the archive bucket. Lets a
// preparer read a draft workbook before approving the batch without depending on
// the browser-side presigned GET. `sheet` selects a tab; omitted, the BE returns
// the workbook's active sheet (the filled form).

export function useReportPreview(
  batchId: string | undefined,
  reportId: string | undefined,
  sheet?: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    // Keyed under the batch so invalidateBatchData() drops it when a draft is
    // regenerated — the artifact reuses its report_id, so a batch-independent
    // key would keep serving the pre-regeneration cells.
    queryKey: queryKeys.batches.reportPreview(batchId, reportId, sheet),
    queryFn: ({ signal }) =>
      apiFetch(`/reports/${reportId}/preview`, reportPreviewSchema, {
        signal,
        ...(sheet ? { query: { sheet } } : {}),
      }),
    enabled: !!reportId && options?.enabled !== false,
    // The artifact is immutable while its slot is; a regeneration invalidates
    // the batch surface, which clears this with it.
    staleTime: 5 * 60_000,
  });
}

// ── Equipment: calibration-due (BE: GET /equipment/calibration-due) ────────
//
// Returns equipment with calibration overdue or due within the lead
// window. Used by the dashboard widget at the top of /batches.

const calibrationDueResponseSchema = z.array(calibrationDueItemSchema);

export function useCalibrationDue(withinDays = 30) {
  return useQuery({
    queryKey: ["equipment", "calibration-due", withinDays],
    queryFn: ({ signal }) =>
      apiFetch("/equipment/calibration-due", calibrationDueResponseSchema, {
        signal,
        query: { within_days: withinDays },
      }),
    staleTime: 5 * 60_000,
  });
}

// ── Data visualization (MSW-first; BE delivery in flight) ──────────────────
//
// Three read-only endpoints behind the `/data` Explorer and the batch Data
// tab. All four roles can read (no RoleGate). Currently served by MSW; when
// DATA_VISUALIZATION_BACKEND_PLAN.md lands, the MSW handlers are removed and
// these same calls hit FastAPI unchanged.

export function useDataSources() {
  return useQuery({
    queryKey: queryKeys.data.sources(),
    queryFn: ({ signal }) => apiFetch("/data/sources", sourcesCatalogResponseSchema, { signal }),
    // The catalog is effectively static config — refetch rarely.
    staleTime: 60 * 60_000,
  });
}

/** One (role, facility, window, granularity) selection to fetch as a series. */
export type SeriesParams = {
  role: string;
  /** Required for dairy-scoped roles; omitted (or com.aemetis.hub) for hub. */
  facility?: string;
  /** Inclusive PT gas-days, YYYY-MM-DD. */
  start: string;
  end: string;
  /** "daily" (default) or "hourly" (raw-totalizer roles only). */
  granularity?: "daily" | "hourly";
};

/**
 * StaleTime policy: a window that ends before today (PT) is cached forever and
 * a window touching today refetches on the ~60s cadence as the in-progress day
 * fills in. "Forever" is safe because the raw gas-day values only move when an
 * upload is accepted/replaced, a correction is approved, or a period is
 * backfilled — and every one of those paths invalidates `queryKeys.data` via
 * `invalidateBatchData`, which forces a refetch regardless of staleTime.
 */
function seriesStaleTime(end: string): number {
  return end < todayPacific() ? Number.POSITIVE_INFINITY : 60_000;
}

function seriesQueryFn(p: SeriesParams) {
  return ({ signal }: { signal: AbortSignal }) =>
    apiFetch("/data/series", seriesResponseSchema, {
      signal,
      query: {
        role: p.role,
        facility: p.facility,
        start: p.start,
        end: p.end,
        granularity: p.granularity ?? "daily",
      },
    });
}

export function useDataSeries(params: SeriesParams | null) {
  return useQuery({
    queryKey: params
      ? queryKeys.data.series(
          params.role,
          params.facility,
          params.start,
          params.end,
          params.granularity ?? "daily",
        )
      : [...queryKeys.data.all, "series", "disabled"],
    queryFn: params ? seriesQueryFn(params) : undefined,
    enabled: !!params,
    staleTime: params ? seriesStaleTime(params.end) : 0,
  });
}

/**
 * Parallel multi-series fetch — one `GET /data/series` per selection, exactly
 * as the backend intends (no batch param in v1; the browser caps concurrency).
 * Per-selection error states are preserved: one failing facility surfaces its
 * own error chip without blanking the others.
 */
export function useDataSeriesMany(selections: SeriesParams[]) {
  return useQueries({
    queries: selections.map((p) => ({
      queryKey: queryKeys.data.series(p.role, p.facility, p.start, p.end, p.granularity ?? "daily"),
      queryFn: seriesQueryFn(p),
      staleTime: seriesStaleTime(p.end),
    })),
  });
}

/**
 * Window-scoped corrections for the data views' overlay (GET /data/corrections):
 * every correction — any batch, any status — whose period overlaps the inclusive
 * PT gas-day window. The batch Data tab uses the batch-scoped
 * `useBatchCorrections` instead; this one exists for the Explorer, which has no
 * batch context. Same wire shape, same zod schema.
 */
export function useDataCorrections(params: { start: string; end: string } | null) {
  return useQuery({
    queryKey: params
      ? queryKeys.data.corrections(params.start, params.end)
      : [...queryKeys.data.all, "corrections", "disabled"],
    queryFn: ({ signal }) =>
      apiFetch("/data/corrections", correctionListSchema, {
        signal,
        query: { start: params?.start, end: params?.end },
      }),
    enabled: !!params,
    // Corrections mutate (propose/approve/reject) — a short staleTime plus the
    // invalidateBatchData prefix invalidation keeps the overlay current.
    staleTime: 60_000,
  });
}

export type UploadRowsPage = { limit?: number; offset?: number };

export function useUploadRows(
  uploadId: string | undefined,
  { limit = 200, offset = 0 }: UploadRowsPage = {},
) {
  return useQuery({
    queryKey: queryKeys.uploads.rows(uploadId, limit, offset),
    queryFn: ({ signal }) =>
      apiFetch(`/uploads/${uploadId}/rows`, uploadRowsResponseSchema, {
        signal,
        query: { limit, offset },
      }),
    enabled: !!uploadId,
    // Keep the prior page visible while the next page loads (no flicker).
    placeholderData: (prev) => prev,
  });
}

// ── Reports: RFS + LCFS suites (BE: POST .../reports/{rfs|lcfs}/generate) ──
//
// Siblings of useGenerateEmtsReport: each returns the full ReportMetadata[]
// (RFS = ten artifacts across five reports; LCFS = fourteen across seven).
// Idempotency-Key only, no X-Reason (handled by apiFetch). Auditor-forbidden
// server-side; the FE hides the buttons for roles that can't generate.

export function useGenerateRfsReport() {
  return useMutation({
    mutationFn: ({ batchId }: { batchId: string }) =>
      apiFetch(`/batches/${batchId}/reports/rfs/generate`, emtsGenerateResponseSchema, {
        method: "POST",
        body: {},
      }),
  });
}

// ── Jobs (BIO-308 #4) ───────────────────────────────────────────────────────
// A cold RFS suite render exceeds a reasonable request budget, so generation
// returns 202 + a job id and the client polls. Polling is the authoritative
// signal by design: the BE's event bus drops events for a slow consumer, so a
// terminal done/error event can be lost.

const jobSchema = z.object({
  job_id: z.string(),
  kind: z.string(),
  batch_id: z.string().nullable(),
  state: z.enum(["pending", "running", "done", "error"]),
  progress: z.number(),
  total: z.number().nullable(),
  result: z.record(z.unknown()).nullable(),
  // Which phase the job is in, for jobs whose work has phases rather than
  // countable artifacts (a batch recompute). Null for the report suites, which
  // report progress/total. Optional so an older backend still parses.
  stage: z.string().nullable().optional(),
  last_error: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  finished_at: z.string().nullable(),
});

export type ComplianceJob = z.infer<typeof jobSchema>;

// Both the start ack and each status poll should always be fast (the start
// endpoint only acknowledges the background job; a status check is a cheap
// read) — bound each generously against a wedged connection (accepted but
// never answered) rather than leaving a request neither resolved nor
// rejected. For the start mutation specifically, that matters beyond just
// UX: without a bound, the same-idempotency-key retry path above can never
// run, since nothing ever actually fails.
//
// Shared by both suite starters and the poll (BIO-346) — the bound is a
// property of "this call only acknowledges or reads", not of which suite.
const SUITE_JOB_REQUEST_TIMEOUT_MS = 10_000;

export function useStartRfsSuiteJob() {
  return useMutation({
    mutationFn: ({ batchId, idempotencyKey }: { batchId: string; idempotencyKey?: string }) =>
      apiFetch(`/batches/${batchId}/reports/rfs/generate-async`, jobSchema, {
        method: "POST",
        body: {},
        idempotencyKey,
        timeout: SUITE_JOB_REQUEST_TIMEOUT_MS,
      }),
  });
}

/**
 * Start the LCFS package as a job (BIO-346).
 *
 * The LCFS render is the one that provably outran the backend's 300s ALB idle
 * timeout on prod: three Q4 2025 packages sealed their artifacts and committed
 * while the caller's response was severed mid-render, so the operator retried
 * with fresh idempotency keys and started concurrent duplicates instead. Same
 * shape and same reused-key-on-retry contract as the RFS starter above.
 */
export function useStartLcfsSuiteJob() {
  return useMutation({
    mutationFn: ({ batchId, idempotencyKey }: { batchId: string; idempotencyKey?: string }) =>
      apiFetch(`/batches/${batchId}/reports/lcfs/generate-async`, jobSchema, {
        method: "POST",
        body: {},
        idempotencyKey,
        timeout: SUITE_JOB_REQUEST_TIMEOUT_MS,
      }),
  });
}

/**
 * Poll one job until it reaches a terminal state.
 *
 * `enabled` is what stops the poll: passing `null` (no job in flight) disables
 * the query entirely, and the interval returns false once the job finishes, so
 * a completed job stops costing requests rather than polling forever.
 */
/**
 * Start a batch recompute (POST /batches/{id}/recompute).
 *
 * Answers 202 with a job to poll, not the recomputed batch: it makes four
 * whole-period DUT reads, and it used to hold the request open for all of them
 * with no status row anywhere, so an in-flight recompute was indistinguishable
 * from none and a failed one left no trace. The refusals the backend can decide
 * without doing the work — unknown batch, locked figures, a recompute already
 * running — still come back inline as errors on this mutation.
 *
 * Bounded by the same timeout as the suite starters: this only acknowledges.
 *
 * `idempotencyKey` is the caller's to keep, for the same reason the suite
 * starters take one: the backend reserves the slot before scheduling any work
 * and replays the original `JobRecord` for a repeat of the same key, so a retry
 * after a lost 202 recovers the job the first attempt started. Omitting it mints
 * a fresh key per attempt, which starts a second whole-period DUT fan-out and
 * publishes a redundant calc revision — the exact case the reservation exists
 * to close.
 */
export function useStartRecompute() {
  return useMutation({
    mutationFn: ({
      batchId,
      reason,
      idempotencyKey,
    }: { batchId: string; reason: string; idempotencyKey?: string }) =>
      apiFetch(`/batches/${batchId}/recompute`, jobSchema, {
        method: "POST",
        reason,
        body: {},
        idempotencyKey,
        timeout: SUITE_JOB_REQUEST_TIMEOUT_MS,
      }),
  });
}

export function useJob(jobId: string | null) {
  return useQuery({
    queryKey: ["jobs", jobId],
    enabled: jobId !== null,
    queryFn: ({ signal }) =>
      apiFetch(`/jobs/${jobId}`, jobSchema, { signal, timeout: SUITE_JOB_REQUEST_TIMEOUT_MS }),
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state === "done" || state === "error" ? false : 2000;
    },
  });
}

export function useGenerateLcfsReport() {
  return useMutation({
    mutationFn: ({ batchId }: { batchId: string }) =>
      apiFetch(`/batches/${batchId}/reports/lcfs/generate`, emtsGenerateResponseSchema, {
        method: "POST",
        body: {},
      }),
  });
}

// ── RFS4800 quarterly report (BE: POST/GET /quarters/{year}/{quarter}/reports) ─
//
// The EPA RFS4800 quarterly production report (form 5900-638), rolled up from the
// quarter's monthly batches. Generate seals + uploads one .xlsx (recorded under
// the quarter's closing-month batch) and returns its ReportMetadata; the list
// surfaces it for download via the standard signed-URL flow. Idempotency-Key on
// generate, no X-Reason (handled by apiFetch); auditor-forbidden BE-side.

export function useGenerateRfs4800Report() {
  return useMutation({
    mutationFn: ({
      year,
      quarter,
      batchIds,
    }: {
      year: number;
      quarter: number;
      batchIds: string[];
    }) =>
      apiFetch(
        `/quarters/${year}/${quarter}/reports/rfs4800/generate`,
        emtsGenerateResponseSchema,
        { method: "POST", body: { batch_ids: batchIds } },
      ),
  });
}

export function useQuarterReports(year: number | undefined, quarter: number | undefined) {
  return useQuery({
    queryKey: queryKeys.quarters.reports(year, quarter),
    queryFn: ({ signal }) =>
      apiFetch(`/quarters/${year}/${quarter}/reports`, reportListArraySchema, {
        signal,
      }),
    enabled: year != null && quarter != null,
  });
}

// ── LCFS quarterly CARB filing (BE: POST .../quarters/{y}/{q}/reports/lcfs/*) ──
//
// Two calls. The preview reads the partner's quarterly station workbook and sets
// it beside the ledger — writing nothing — so an FSE id the roster does not know
// can be resolved by a person. Generate then takes the confirmed per-station
// totals as figures and records both CARB workbooks (fuel transactions + total
// throughput) under the quarter's closing-month batch, listed by
// `useQuarterReports` beside RFS4800.

export function usePreviewLcfsThroughput() {
  return useMutation({
    mutationFn: ({ year, quarter, file }: { year: number; quarter: number; file: File }) => {
      const form = new FormData();
      form.append("file", file);
      return apiFetchMultipart(
        `/quarters/${year}/${quarter}/reports/lcfs/throughput-preview`,
        form,
        lcfsThroughputPreviewSchema,
        { reason: `Preview ${file.name}` },
      );
    },
  });
}

export function useGenerateLcfsQuarter() {
  return useMutation({
    mutationFn: ({
      year,
      quarter,
      throughput,
      acknowledgedShortfalls = [],
    }: {
      year: number;
      quarter: number;
      throughput: { fse_id: string; therms: string }[];
      /** Shortfalls from the preview the operator chose to file without, verbatim. */
      acknowledgedShortfalls?: { period: string; fse_id: string; mmbtu: string }[];
    }) =>
      apiFetch(`/quarters/${year}/${quarter}/reports/lcfs/generate`, emtsGenerateResponseSchema, {
        method: "POST",
        // Sent only when there is something to acknowledge, so a quarter with no
        // shortfall posts exactly the body it always has.
        body:
          acknowledgedShortfalls.length > 0
            ? { throughput, acknowledged_shortfalls: acknowledgedShortfalls }
            : { throughput },
      }),
  });
}

// ── RFS0902 annual report (BE: POST/GET /years/{year}/reports[/rfs0902]) ───────

export function useGenerateRfs0902Report() {
  return useMutation({
    mutationFn: ({
      year,
      batchIds,
    }: {
      year: number;
      batchIds: string[];
    }) =>
      apiFetch(`/years/${year}/reports/rfs0902/generate`, emtsGenerateResponseSchema, {
        method: "POST",
        body: { batch_ids: batchIds },
      }),
  });
}

export function useYearReports(year: number | undefined) {
  return useQuery({
    queryKey: queryKeys.years.reports(year),
    queryFn: ({ signal }) => apiFetch(`/years/${year}/reports`, reportListArraySchema, { signal }),
    enabled: year != null,
  });
}

// ── Issues: run the validation engine (BE: POST /batches/{id}/validate) ────
//
// Re-runs the data-quality rules over the batch and persists any new findings
// (idempotent on dedup_key — never resets triage). Returns counts only; callers
// invalidate the batch issues list to pick up new rows. X-Reason required;
// auditor-forbidden server-side.

// Validation fans out to the per-facility DUT server, which can be slow or
// (on a dropped VPN) unreachable, so the backend now runs it in the background
// and answers POST /validate with a 202 ack (validationStartedSchema) instead
// of the run counts. The request itself returns quickly; the optional caller
// AbortSignal still lets the user cancel the in-flight ack, and a short client
// timeout guards against a wedged accept (the long DUT work no longer blocks
// the response). Callers refresh the batch surface on a delay to pick up the
// findings the background run writes.
const VALIDATE_TIMEOUT_MS = 30_000;

export function useValidateBatch() {
  return useMutation({
    mutationFn: ({
      batchId,
      reason,
      signal,
    }: {
      batchId: string;
      reason: string;
      signal?: AbortSignal;
    }) =>
      apiFetch(`/batches/${batchId}/validate`, validationStartedSchema, {
        method: "POST",
        reason,
        body: {},
        signal,
        timeout: VALIDATE_TIMEOUT_MS,
      }),
  });
}

// ── Uploads: accept a parsed upload into a batch (BE: POST .../accept) ─────
//
// Links a parse-clean upload to a batch, bumps data_version, and re-runs the
// RFS calc. Body carries the target batch_id; X-Reason required. A second
// accept of the same upload is a clean 409 IDEMPOTENCY_CONFLICT (the upload has
// already left "parsed"). Auditor-forbidden server-side.

export function useAcceptUpload() {
  return useMutation({
    mutationFn: ({
      uploadId,
      batchId,
      reason,
    }: {
      uploadId: string;
      batchId: string;
      reason: string;
    }) =>
      apiFetch(`/uploads/${uploadId}/accept`, uploadAcceptResponseSchema, {
        method: "POST",
        reason,
        body: { batch_id: batchId },
      }),
  });
}

// ── Backfill (BE: POST /batches/{id}/backfill[/certify]) ───────────────────
//
// Reviewer/admin-only. There is no GET — both mutations return the current
// BackfillStatus, which the UI caches under queryKeys.batches.backfill so the
// controls reflect state within the session. X-Reason required.

export function useMarkBackfill() {
  return useMutation({
    mutationFn: ({ batchId, reason }: { batchId: string; reason: string }) =>
      apiFetch(`/batches/${batchId}/backfill`, backfillStatusSchema, {
        method: "POST",
        reason,
        body: {},
      }),
  });
}

export function useCertifyBackfill() {
  return useMutation({
    mutationFn: ({ batchId, reason }: { batchId: string; reason: string }) =>
      apiFetch(`/batches/${batchId}/backfill/certify`, backfillStatusSchema, {
        method: "POST",
        reason,
        body: {},
      }),
  });
}

// ── Workbook comparison (BE: GET/POST /batches/{id}/workbook-comparison) ───
//
// GET returns the recorded cell variances (any role). POST records a batch of
// cells and returns the computed variances (±0.01% tolerance verdicts);
// reviewer/admin-only, X-Reason required. platform_value / workbook_value are
// decimal-as-string.

const workbookVarianceListSchema = z.array(workbookVarianceSchema);

export function useWorkbookComparison(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.batches.workbook(batchId),
    queryFn: ({ signal }) =>
      apiFetch(`/batches/${batchId}/workbook-comparison`, workbookVarianceListSchema, { signal }),
    enabled: !!batchId,
  });
}

export function useRecordWorkbookComparison() {
  return useMutation({
    mutationFn: ({
      batchId,
      input,
      reason,
    }: {
      batchId: string;
      input: WorkbookComparisonRequest;
      reason: string;
    }) =>
      apiFetch(`/batches/${batchId}/workbook-comparison`, workbookVarianceListSchema, {
        method: "POST",
        reason,
        body: input,
      }),
  });
}

// ── Notifications (BE: /notifications/*) ───────────────────────────────────
//
// Topic catalog + the caller's effective subscriptions. All read endpoints are
// any-role; setting a subscription is self-service (no X-Reason — it's not an
// audit-critical mutation).

const topicListSchema = z.array(topicInfoSchema);
const subscriptionListSchema = z.array(subscriptionStateSchema);

export function useNotificationTopics() {
  return useQuery({
    queryKey: queryKeys.notifications.topics(),
    queryFn: ({ signal }) => apiFetch("/notifications/topics", topicListSchema, { signal }),
    // The catalog is effectively static config.
    staleTime: 60 * 60_000,
  });
}

export function useNotificationSubscriptions() {
  return useQuery({
    queryKey: queryKeys.notifications.subscriptions(),
    queryFn: ({ signal }) =>
      apiFetch("/notifications/subscriptions", subscriptionListSchema, { signal }),
  });
}

export function useSetSubscription() {
  return useMutation({
    mutationFn: ({ topicKey, enabled }: { topicKey: string; enabled: boolean }) =>
      apiFetch(`/notifications/subscriptions/${topicKey}`, subscriptionStateSchema, {
        method: "PUT",
        body: { enabled },
      }),
  });
}

export type { SeriesResponse };

// ── LCFS: FSE registry + pathway exposure (FRONTEND-FIRST) ──────────────────
//
// No omai endpoint exists for any of these yet; they are served by the MSW
// handlers behind VITE_USE_LCFS_MSW. Shapes follow the contract's conventions,
// so when the backend lands these hooks should not need to change.

const fseRegistryListSchema = listResponseSchema(fseRecordSchema);

const dairyRegistryListSchema = listResponseSchema(dairyRecordSchema);

/** The dairy roster — the supply side of the allocation, in draw order. */
export function useDairyRegistry() {
  return useQuery({
    queryKey: queryKeys.lcfs.dairies(),
    queryFn: ({ signal }) => apiFetch("/lcfs/dairies", dairyRegistryListSchema, { signal }),
  });
}

/** The canonical FSE roster. Current-state, not period-scoped. */
export function useFseRegistry() {
  return useQuery({
    queryKey: queryKeys.lcfs.fse(),
    queryFn: ({ signal }) => apiFetch("/lcfs/fse", fseRegistryListSchema, { signal }),
  });
}

// ── LCFS: allocation ledger + lot inventory ─────────────────────────────────
//
// The engine runs in the browser over the stored inputs rather than against a
// computed endpoint. `/lcfs/allocation` and `/lcfs/allocation/lots` do not
// exist server-side; the four lists below do, and `lib/allocationView.ts`
// merges them exactly as the backend would.
//
// One query for all four, because they are one thing: replaying every period
// from the beginning is the only correct way to allocate one of them — a
// period's opening balance is the previous period's close — so the whole
// history is built once, cached under one key, and both screens read off it.
// Splitting them would mean four loading states for a single table.
//
// Porting the merge to Python is the remaining half of this move (locked
// decision #6: the UI should never recompute a compliance figure). Until then
// the two implementations agreeing is itself worth something — the TypeScript
// one is pinned against figures aggregated independently from the source
// workbook.

const dairyProductionListSchema = listResponseSchema(dairyProductionRowSchema);
const stationCapacityListSchema = listResponseSchema(stationCapacityRowSchema);
const periodListSchema = z.array(z.string());

/**
 * `enabled` exists for the drill-downs, not for the ledger.
 *
 * The two registry tabs fetch one list each. Their drawers read the allocation,
 * which is five lists and a seventeen-period replay — work nobody asked for
 * until a row is actually clicked. Gating on the selection keeps opening the FSE
 * or dairy roster exactly as cheap as it was, and the first click warms the same
 * cache entry the ledger tab uses.
 */
function useAllocationInputs(enabled = true) {
  return useQuery({
    queryKey: queryKeys.lcfs.inputs(),
    enabled,
    queryFn: async ({ signal }) => {
      const [dairies, stations, production, capacities, periods] = await Promise.all([
        apiFetch("/lcfs/dairies", dairyRegistryListSchema, { signal }),
        apiFetch("/lcfs/fse", fseRegistryListSchema, { signal }),
        apiFetch("/lcfs/production", dairyProductionListSchema, { signal }),
        apiFetch("/lcfs/capacities", stationCapacityListSchema, { signal }),
        apiFetch("/lcfs/periods", periodListSchema, { signal }),
      ]);
      return buildAllocation({
        dairies: dairies.items,
        stations: stations.items,
        production: production.items,
        capacities: capacities.items,
        periods,
      });
    },
    // Reference data, and the build walks 17 periods. Re-running it on every
    // remount of a tab the reader is switching between is pure waste.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Turn "that period was never allocated" into a query error.
 *
 * The screens already render `isError` with the message, and a period the
 * engine cannot serve is exactly the case a reader needs told — silently
 * rendering an empty ledger for a mistyped or stale `?period=` would read as
 * "nothing was dispensed", which is a different and wrong statement.
 */
function withMissingPeriodAsError<T>(
  query: UseQueryResult<BuiltAllocation>,
  view: T | null | undefined,
  period: string | undefined,
) {
  const missing = query.data !== undefined && view === null;
  return {
    ...query,
    data: missing ? undefined : (view ?? undefined),
    isError: query.isError || missing,
    error: missing
      ? new Error(`No allocation for ${period}. Available: ${query.data?.periods.join(", ")}.`)
      : query.error,
  };
}

export function useAllocationPeriod(period: string | undefined) {
  const query = useAllocationInputs();
  const view = useMemo(
    () =>
      query.data ? allocationPeriodView(query.data, period ?? query.data.latestPeriod) : undefined,
    [query.data, period],
  );
  return withMissingPeriodAsError(query, view, period);
}

export function useLotInventory(period: string | undefined) {
  const query = useAllocationInputs();
  const view = useMemo(
    () =>
      query.data ? lotInventoryView(query.data, period ?? query.data.latestPeriod) : undefined,
    [query.data, period],
  );
  return withMissingPeriodAsError(query, view, period);
}

// ── LCFS: station + dairy drill-downs ───────────────────────────────────────
//
// Both read the SAME cache entry the ledger built — no new endpoint, no new
// query key, no refetch. `runAllocation` already replays every period to
// allocate any one of them, so a party's whole history is a filter over a result
// that is sitting in memory. Passing `null` disables the fetch entirely, which
// is what keeps a closed drawer free.

/** One station's allocation history, or `undefined` while nothing is selected. */
export function useStationHistory(fseId: string | null) {
  const query = useAllocationInputs(fseId !== null);
  const view = useMemo(
    () => (query.data && fseId ? stationHistoryView(query.data, fseId) : undefined),
    [query.data, fseId],
  );
  // A station id the roster does not carry is a broken link, not an empty
  // history — and `stationHistoryView` already distinguishes the two, so a null
  // here is worth surfacing as an error rather than rendering as "no volume".
  return unknownIdAsError(query, view, fseId, "station");
}

/** One dairy's production and sending history. */
export function useDairyHistory(dairyId: string | null) {
  const query = useAllocationInputs(dairyId !== null);
  const view = useMemo(
    () => (query.data && dairyId ? dairyHistoryView(query.data, dairyId) : undefined),
    [query.data, dairyId],
  );
  return unknownIdAsError(query, view, dairyId, "dairy");
}

// ── LCFS: loading a period from the partner's allocation workbook ──────────
//
// Two calls, and the split between them is the safety property. The preview
// writes nothing and is what the operator answers the resolution on; the load
// refuses unless every site resolved. The second is the first multipart route in
// this app to answer with a job rather than a result — deriving the period's
// production takes minutes against live meters, so the 202 hands back a handle.

/**
 * What a workbook would load, and for which month (POST /lcfs/allocation/preview).
 *
 * The first call sends no period: the answer is the workbook's newest month
 * that reads, plus `months` — every month sheet the file carries. The screen
 * then asks for any other ticked month by name ({@link useStationAllocationPreviews}).
 * Either way `period` is read off the sheet's own dates, never typed.
 *
 * Writes nothing: no archive, no rows, no aliases. Takes no audit reason for the
 * same reason `useExtractCalibrationCert` does not — demanding one for a read
 * teaches people to type a reason without meaning it.
 */
export function usePreviewStationAllocation() {
  return useMutation({
    mutationFn: ({ file, period }: { file: File; period?: string }) =>
      previewStationAllocation(file, period),
  });
}

function previewStationAllocation(file: File, period?: string) {
  const form = new FormData();
  form.append("file", file);
  if (period) form.append("period", period);
  return apiFetchMultipart("/lcfs/allocation/preview", form, allocationPreviewSchema, {
    reason: `Preview ${file.name}`,
  });
}

/**
 * The preview of each ticked month other than the one the drop already answered.
 *
 * Queries rather than mutations because each month is its own loading and error
 * state, and a month unticked then ticked again should not re-post the file.
 * `fileKey` changes on every drop, so a new file never reads an old one's answer.
 */
export function useStationAllocationPreviews(
  file: File | null,
  fileKey: number,
  periods: string[],
) {
  return useQueries({
    queries: periods.map((period) => ({
      queryKey: queryKeys.lcfsAllocationPreview(fileKey, period),
      queryFn: () => previewStationAllocation(file as File, period),
      enabled: file !== null,
      staleTime: Number.POSITIVE_INFINITY,
      retry: false,
    })),
  });
}

/**
 * Load one or more months, and start deriving their production (POST /lcfs/allocation).
 *
 * `periods` goes on the form as a repeated `period` field. The months commit
 * together — the backend refuses them all if a site on any one is unresolved —
 * and one production job derives them in turn.
 *
 * `mappings` carries the operator's answers for whatever the preview could not
 * resolve, keyed on the site name exactly as the sheet spells it — that is what
 * they were shown. `newStations` carries any they chose to create instead.
 *
 * Answers 202 with a `JobRecord`: the capacities are committed by the time this
 * resolves, and the production job is what the caller then polls. The two are
 * deliberately not one unit — a DUT outage must not throw away a good upload.
 */
export function useLoadStationAllocation() {
  return useMutation({
    mutationFn: ({
      file,
      periods,
      reason,
      mappings,
      newStations,
    }: {
      file: File;
      periods: string[];
      reason: string;
      mappings?: Record<string, string>;
      newStations?: NewStationInput[];
    }) => {
      const form = new FormData();
      form.append("file", file);
      for (const period of periods) form.append("period", period);
      if (mappings && Object.keys(mappings).length > 0) {
        form.append("mappings", JSON.stringify(mappings));
      }
      if (newStations && newStations.length > 0) {
        form.append("new_stations", JSON.stringify(newStations));
      }
      return apiFetchMultipart("/lcfs/allocation", form, jobSchema, { reason });
    },
  });
}

/** The drill-down twin of {@link withMissingPeriodAsError}. */
function unknownIdAsError<T>(
  query: UseQueryResult<BuiltAllocation>,
  view: T | null | undefined,
  id: string | null,
  kind: "station" | "dairy",
) {
  const missing = query.data !== undefined && view === null;
  return {
    ...query,
    data: missing ? undefined : (view ?? undefined),
    isError: query.isError || missing,
    error: missing ? new Error(`No ${kind} ${id} in the LCFS roster.`) : query.error,
  };
}

// ── LCFS: a period's utility-statement support ──────────────────────────────

/**
 * Whether one month's statements are all in.
 *
 * No `refetchInterval`, unlike `useValidationStatus`: there is no background run
 * to wait for. The backend derives the verdict on every read, so the only things
 * that change it are actions taken on this page, and those invalidate it.
 */
export function useLcfsPeriodValidation(period: string | undefined) {
  return useQuery({
    queryKey: queryKeys.lcfs.validation(period ?? ""),
    queryFn: () =>
      apiFetch(
        `/lcfs/periods/${encodeURIComponent(period as string)}/validation`,
        lcfsPeriodValidationSchema,
      ),
    enabled: Boolean(period),
  });
}

/** The statements already filed for one month. */
export function useLcfsPeriodStatements(period: string | undefined) {
  return useQuery({
    queryKey: queryKeys.lcfs.statements(period ?? ""),
    queryFn: () =>
      apiFetch(
        `/lcfs/periods/${encodeURIComponent(period as string)}/statements`,
        z.array(utilityStatementRecordSchema),
      ),
    enabled: Boolean(period),
  });
}

/**
 * File one station's statement for one month.
 *
 * `gas_meter_number` is checked against the station's roster meter server-side
 * and the call is refused on a mismatch, so a 422 here is the control working
 * rather than a bug — the message names both numbers.
 */
export function useFileUtilityStatement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      period,
      fseId,
      gasMeterNumber,
      accountNumber,
      statementStart,
      statementEnd,
      reason,
    }: {
      file: File;
      period: string;
      fseId: string;
      gasMeterNumber: string;
      accountNumber?: string;
      statementStart?: string;
      statementEnd?: string;
      reason: string;
    }) => {
      const form = new FormData();
      form.append("file", file);
      form.append("period", period);
      form.append("fse_id", fseId);
      form.append("gas_meter_number", gasMeterNumber);
      if (accountNumber) form.append("account_number", accountNumber);
      if (statementStart) form.append("statement_start", statementStart);
      if (statementEnd) form.append("statement_end", statementEnd);
      // Beside `POST /lcfs/allocation`, not under /uploads: the partner sends
      // the ledger workbook and its supporting statements together, and both
      // are filed from the LCFS screen.
      return apiFetchMultipart("/lcfs/statements", form, filedUtilityStatementSchema, {
        reason,
      });
    },
    onSuccess: (filed) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.lcfs.validation(filed.period),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.lcfs.statements(filed.period),
      });
      // The PDF is an ordinary manual_uploads row, so the uploads views list it.
      queryClient.invalidateQueries({ queryKey: queryKeys.uploads.all });
    },
  });
}

// ── LCFS: the partner's executed transaction confirmations ──────────────────

/** Every confirmation on file, newest execution first. */
export function useLcfsConfirmations() {
  return useQuery({
    queryKey: queryKeys.lcfs.confirmations(),
    queryFn: () => apiFetch("/lcfs/confirmations", z.array(transactionConfirmationRecordSchema)),
  });
}

/**
 * Read a confirmation's month table and execution date. Writes nothing.
 *
 * Called on drop so the execution date can be **shown** rather than typed. It
 * decides which revision is authoritative, and nothing on the face of the
 * document orders revisions correctly — so the field a person was least
 * equipped to get right is the one they no longer have to.
 */
export function usePreviewConfirmation() {
  return useMutation({
    mutationFn: ({ file }: { file: File }) => {
      const form = new FormData();
      form.append("file", file);
      return apiFetchMultipart("/lcfs/confirmations/preview", form, confirmationPreviewSchema, {
        reason: `Preview ${file.name}`,
      });
    },
  });
}

/**
 * File an executed Monthly RNG Confirmation.
 *
 * The month table is parsed server-side; the only field asked of a person is
 * `executedOn`, because it decides which revision is authoritative and nothing
 * printed on the document orders them correctly.
 *
 * A confirmation that contradicts a loaded period is stored, not refused — the
 * response carries the disagreements so the screen can say so straight away
 * rather than waiting for the next validation run. Every period's verdict is
 * invalidated because one document can misstate any month of the term.
 */
export function useFileTransactionConfirmation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      executedOn,
      reason,
    }: {
      file: File;
      /** Omitted when the document carries its own completion stamp. */
      executedOn?: string;
      reason: string;
    }) => {
      const form = new FormData();
      form.append("file", file);
      if (executedOn) form.append("executed_on", executedOn);
      return apiFetchMultipart("/lcfs/confirmations", form, filedConfirmationSchema, {
        reason,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lcfs.confirmations() });
      // Any month of the term may now read differently.
      queryClient.invalidateQueries({ queryKey: queryKeys.lcfs.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.uploads.all });
    },
  });
}
